import { useState } from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from '@mui/material';
import * as constantes from '../../common/Constantes';
import * as yup from "yup";
import Utilities from '../../common/Utilities';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ConfigService from '../../services/ConfigService';
import ConfigUtils from '../../utils/Config';
import { CONFIGTYPES } from '../../types/Config';
import IEntidadFacturacion from '../../types/EntidadFacturacion';
import CambioTitularDialog, { mensajeDeError, ModoCambioTitular, pideCambioTitular } from './CambioTitularDialog';
import { ICambioTitularOpciones } from '../../types/EntidadFacturacion';
import { getAnioEnCurso } from '../../utils/Numeracion';

interface EntidadFacturacionFormProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  entidad?: IEntidadFacturacion; // undefined = alta
  onSuccess: (entidad: IEntidadFacturacion) => void;
}

const schema = yup.object({
  nombre: yup.string().required('El nombre o razón social es obligatorio'),
  nif: yup.string()
    .required('El NIF es obligatorio')
    .test('nif-validation', 'El NIF no es válido', function (value) {
      const pais = this.parent.pais || 'ES';
      if (Utilities.validateTaxId(value || '', pais)) return true;
      return this.createError({ message: `${Utilities.getNifLabel(pais)} no válido` });
    }),
  calle: yup.string().required('La calle es obligatoria'),
  ciudad: yup.string().required('La ciudad es obligatoria'),
  codigoPostal: yup.string()
    .required('El código postal es obligatorio')
    .test('cp-validation', 'El código postal no tiene un formato válido', function (value) {
      return Utilities.validatePostalCode(value || '', this.parent.pais || 'ES');
    }),
  pais: yup.string().required('El país es obligatorio'),
  moneda: yup.string().required('La divisa es obligatoria'),
  email: yup.string().email('El email no es válido').optional(),
});

const EntidadFacturacionForm = ({ open, onClose, userId, entidad, onSuccess }: EntidadFacturacionFormProps) => {
  const isEdit = !!entidad;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cambio de NIF de una entidad que ya factura (cambio de titular): el servidor pide la serie nueva y se reintenta
  const [cambioTitular, setCambioTitular] = useState<{ modo: ModoCambioTitular; mensaje: string; data: yup.InferType<typeof schema> } | null>(null);

  const [seriePrefix, setSeriePrefix] = useState('S');
  const [counter, setCounter] = useState(0);
  const [serieError, setSerieError] = useState<string | undefined>(undefined);
  const [counterError, setCounterError] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<yup.InferType<typeof schema>>({
    defaultValues: entidad ?? { nombre: '', nif: '', calle: '', ciudad: '', codigoPostal: '', pais: 'ES', moneda: 'EUR', email: '' },
    resolver: yupResolver(schema)
  });

  const pais = watch('pais');
  const nifLabel = Utilities.getNifLabel(pais);
  // Año de la serie: el de la zona horaria del país de la entidad (el servidor lo valida igual)
  const currentYear = getAnioEnCurso(pais);

  const validateSerie = () => {
    let valid = true;

    if (!seriePrefix || seriePrefix.trim() === '') {
      setSerieError('La serie es obligatoria');
      valid = false;
    } else if (!/^[A-Z0-9]+$/.test(seriePrefix)) {
      setSerieError('La serie solo admite letras y números');
      valid = false;
    } else if (seriePrefix.length > 6) {
      setSerieError('El prefijo de la serie admite hasta 6 caracteres');
      valid = false;
    } else {
      setSerieError(undefined);
    }

    if (!Number.isInteger(counter) || counter < 0 || counter > 999999) {
      setCounterError('El número inicial debe estar entre 0 y 999999');
      valid = false;
    } else {
      setCounterError(undefined);
    }

    return valid;
  };

  const formatInvoiceNumber = () => `${seriePrefix}${currentYear}-${(counter + 1).toString().padStart(6, "0")}`;

  // Al editar, el servidor decide si un NIF nuevo es un cambio de titular (y responde 409 para pedir la serie nueva);
  // `opciones` lleva la respuesta del usuario en el reintento. Un cambio de titular añade una entidad desactivada, así
  // que se guarda la lista completa que devuelve el servidor.
  const guardarDatos = async (data: yup.InferType<typeof schema>, opciones: ICambioTitularOpciones = {}) => {
    if (isEdit) {
      const response = await ConfigService.updateEntidadFacturacion(userId, entidad!._id, { ...data, ...opciones });
      const entidades = response.data.datosFacturacionAdicional;
      ConfigUtils.setDatosFacturacionAdicional(entidades);
      setCambioTitular(null);
      onSuccess(entidades.find(e => e._id === entidad!._id) ?? { ...entidad!, ...data });
    } else {
      const fullSerie = `${seriePrefix}${currentYear}`;
      const response = await ConfigService.createEntidadFacturacion({ ...data, userId, serie: fullSerie, counter });
      ConfigUtils.addConfig(CONFIGTYPES.datosFacturacionAdicional.toString(), response.data);
      onSuccess(response.data);
    }
  };

  const onSubmit = async (data: yup.InferType<typeof schema>) => {
    if (!isEdit && !validateSerie()) return;

    setLoading(true);
    setError(null);
    try {
      await guardarDatos(data);
    } catch (err: unknown) {
      const pregunta = pideCambioTitular(err);
      if (pregunta) setCambioTitular({ ...pregunta, data });
      else setError(mensajeDeError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>{isEdit ? 'Editar entidad de facturación' : 'Nueva entidad de facturación'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Controller name="nombre" control={control} render={({ field }) => (
              <TextField {...field} label="Nombre o razón social" fullWidth disabled={loading} error={!!errors.nombre} helperText={errors.nombre?.message} />
            )} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="nif" control={control} render={({ field }) => (
              <TextField {...field} label={nifLabel} fullWidth disabled={loading} error={!!errors.nif} helperText={errors.nif?.message} />
            )} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="pais" control={control} render={({ field }) => (
              <TextField {...field} select label="País" fullWidth disabled={loading} error={!!errors.pais} helperText={errors.pais?.message}>
                {constantes.paises.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            )} />
          </Grid>
          <Grid item xs={12}>
            <Controller name="calle" control={control} render={({ field }) => (
              <TextField {...field} label="Calle / Dirección" fullWidth disabled={loading} error={!!errors.calle} helperText={errors.calle?.message} />
            )} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="ciudad" control={control} render={({ field }) => (
              <TextField {...field} label="Ciudad" fullWidth disabled={loading} error={!!errors.ciudad} helperText={errors.ciudad?.message} />
            )} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="codigoPostal" control={control} render={({ field }) => (
              <TextField {...field} label="Código postal" fullWidth disabled={loading} error={!!errors.codigoPostal} helperText={errors.codigoPostal?.message} />
            )} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="moneda" control={control} render={({ field }) => (
              <TextField {...field} select label="Divisa" fullWidth disabled={loading} error={!!errors.moneda} helperText={errors.moneda?.message}>
                {constantes.SUPPORTED_CURRENCIES.map(({ code, label }) => (
                  <MenuItem key={code} value={code}>{label}</MenuItem>
                ))}
              </TextField>
            )} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="email" control={control} render={({ field }) => (
              <TextField {...field} label="Email de contacto (opcional)" fullWidth disabled={loading} error={!!errors.email} helperText={errors.email?.message} />
            )} />
          </Grid>

          {!isEdit && <>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Define la serie y el número de la primera factura que se emitirá con esta entidad.
                Cada entidad debe tener un prefijo de serie distinto al de las demás (por ejemplo, "S" para
                una y "B" para otra), para que el número de factura identifique siempre a su entidad.
                La serie no podrá modificarse posteriormente.
                El número de factura se incrementará automáticamente con cada factura emitida.
                Si aún no se han emitido facturas, el número inicial aparecerá como <b>0</b>.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Serie (prefijo)*"
                fullWidth
                disabled={loading}
                value={seriePrefix}
                onChange={(e) => setSeriePrefix(e.target.value.toUpperCase())}
                error={!!serieError}
                helperText={serieError}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Número inicial*"
                type="number"
                fullWidth
                disabled={loading}
                value={counter}
                onChange={(e) => setCounter(Number(e.target.value))}
                error={!!counterError}
                helperText={counterError}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1">
                Próxima factura a emitir: <b>{formatInvoiceNumber()}</b>
              </Typography>
            </Grid>
          </>}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>Cancelar</Button>
        <Button disabled={loading} variant="contained" onClick={handleSubmit(onSubmit)}>
          {loading ? <CircularProgress size={20} /> : 'Guardar'}
        </Button>
      </DialogActions>

      {cambioTitular && (
        <CambioTitularDialog
          open
          onClose={() => setCambioTitular(null)}
          modo={cambioTitular.modo}
          mensaje={cambioTitular.mensaje}
          pais={cambioTitular.data.pais}
          onConfirmar={(opciones) => guardarDatos(cambioTitular.data, opciones)}
        />
      )}
    </Dialog>
  );
};

export default EntidadFacturacionForm;
